import React from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../../state/mint/hooks'
import { TokenAmount } from '@uniswap/sdk-core'
import { Field } from '../../../../state/mint/actions'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../../hooks'
import { ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { RowBetween } from '../../../../components/Row'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { parseUnits } from 'ethers/lib/utils'
import { useTokenBalance } from '../../../../state/wallet/hooks'
import { calculateGasMargin, getContract } from '../../../../utils'
import { Text } from 'rebass'
import { HYPERBEAT_TOKEN_ADDRESS } from '@hyperswap/addresses'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'
import { useToken } from 'hooks/Tokens'
import HYPERBEAT_TOKEN_ABI from '../../../../abis/hyperbeat/hbhype.json'
import { Dots } from 'pages/Pool/styleds'
import { useUSDCValue } from 'hooks/useUSDCPrice'
import { usePrivy } from '@privy-io/react-auth'

interface UnstakeProps {
  key?: number
  setRefresh: () => void
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const Unstake: React.FC<UnstakeProps> = ({ setRefresh }) => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const { login } = usePrivy()
  const token = useToken(HYPERBEAT_TOKEN_ADDRESS)
  const tokenBalance = useTokenBalance(account ?? undefined, token ?? undefined)

  const [loadingRemove, setLoadingRemove] = React.useState<boolean>(false)
  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(token ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)
  const fiatValueInput = useUSDCValue(parsedAmounts[Field.CURRENCY_A])

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const addTransaction = useTransactionAdder()

  async function onWithdraw() {
    if (!chainId || !library || !account) return
    const hyperbeatContract = getContract(HYPERBEAT_TOKEN_ADDRESS, HYPERBEAT_TOKEN_ABI.abi, library, account)

    const valueAmount = parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18)

    const estimate = hyperbeatContract.estimateGas.requestRedeem
    const method = hyperbeatContract.requestRedeem
    const args = [valueAmount, account, account]
    setLoadingRemove(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Withdraw hbHYPE`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          await wait(4000)
          setRefresh()
          setLoadingRemove(false)
        })
      )
      .catch((error: any) => {
        setLoadingRemove(false)
        console.error(error)
      })
  }

  const isDisabled =
    loadingRemove === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    (tokenBalance && parseFloat(formattedAmounts[Field.CURRENCY_A]) > parseFloat(tokenBalance?.toExact()))

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id="unstake-hbhype"
        showCommonBases
        smallTokenName
        fiatValue={fiatValueInput}
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight
            onClick={() =>
              login({
                loginMethods: ['wallet'],
                walletChainType: 'ethereum-only',
                disableSignup: false,
              })
            }
          >
            Connect Wallet
          </ButtonLight>
        ) : (
          <RowBetween>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onWithdraw()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingRemove ? <Dots>Withdrawing</Dots> : 'Withdraw'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Unstake
